import { useState } from "react";
import CustomButton from "../../components/CustomButton";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import classes from "../../components/Modal.module.css";
import ErrorMessage from "../../components/ErrorMessage";
import saveIcon from "../../assets/save_icon.png";
import CustomInput from "../../components/CustomInput";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import { onlyNums } from "../../utils/OnlyNums";
import Textarea from "../../components/Textarea";
import ErrorBox from "../../components/ErrorBox";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import axiosClient from "../../services/axiosClient";
import HotdropdownV2 from "../../components/HotdropdownV2";

export default function AddEmployeeDeductionModal({
  onClose,
  onSuccess,
  employeeId,
}) {
  const { t } = useTranslation("general");
  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [deduction, setDeduction] = useState({
    deduction: "",
    date: "",
    deductionDate: "",
    amount: 0,
    notes: "",
  });
  const [selectedDeduction, setSelectedDeduction] = useState({
    id: null,
    name: "",
  });
  const [deductions, setDeductions] = useState([]);

  // Fetch available deductions
  const getDeductions = async (currentPage, limit, _, query) => {
    try {
      const response = await axiosClient.get("/deductions/getDeductions", {
        params: {
          limit,
          page: currentPage,
          name: query,
        },
      });

      setDeductions(response.data.deductions);
    } catch (error) {
      console.error(
        "Error fetching deductions:",
        error.response?.data || error
      );
    }
  };

  const handleCancel = () => {
    onClose();
  };

  // Add user deduction
  const handleAddUserDeduction = async () => {
    if (!selectedDeduction.id) {
      setValidationError("deductionRequired");
      return;
    }

    if (!deduction?.date) {
      setValidationError("dateRequired");
      return;
    }

    if (!deduction?.deductionDate) {
      setValidationError("deductionDateRequired");
      return;
    }

    try {
      setLoadingModal(true);
      setError(null);

      const response = await axiosClient.post(
        "/userDeductions/createUserDeduction",
        {
          ...deduction,
          notes: deduction.notes,
          deductionDate: deduction.deductionDate,
          userId: Number(employeeId),
          deductionId: selectedDeduction.id,
        }
      );

      onSuccess();
    } catch (err) {
      console.error("Error creating entitlement:", err);
      setError("entitlementCreationFailed");
    } finally {
      setLoadingModal(false);
    }
  };

  return (
    <>
      <Modal
        title="addDeduction"
        onClose={handleCancel}
        modalSize={"c-modal--small"}
        footer={
          <>
            {loadingModal ? <Loader size={LOADER_SIZES.MEDIUM} /> : null}
            {error ? (
              <ErrorMessage classList="o-row o-row--horizontal-align_end">
                {t(error)}
              </ErrorMessage>
            ) : null}
            {validationError ? (
              <ErrorMessage classList="o-row o-row--horizontal-align_end">
                {t(validationError)}
              </ErrorMessage>
            ) : null}
            <section className={classes.actions}>
              <CustomButton
                onClick={handleAddUserDeduction}
                title={t("confirm")}
                icon={saveIcon}
                iconSize={16}
                disabled={loadingModal}
              />
              <CustomButton
                title={t("cancel")}
                onClick={handleCancel}
                variant="blank"
              />
            </section>
          </>
        }
      >
        {error ? (
          <ErrorBox classList="o-row o-row--horizontal-align_end">
            {t(error)}
          </ErrorBox>
        ) : null}
        {validationError ? (
          <ErrorBox classList="o-row o-row--horizontal-align_end">
            {t(validationError)}
          </ErrorBox>
        ) : null}
        <div className="o-column o-column--gap-size_400">
          <div>
            <label>{t("deduction")}</label>
            <HotdropdownV2
              initOptions={deductions}
              selected={selectedDeduction}
              setSelected={(item) => {
                if (validationError === "deductionRequired") {
                  setValidationError(null);
                }
                setSelectedDeduction(item);
                setDeduction((prev) => ({
                  ...prev,
                  deduction: item?.name,
                }));
              }}
              flex={true}
              errorText={
                validationError === "deductionRequired"
                  ? "deductionRequired"
                  : ""
              }
              getData={getDeductions}
            />
          </div>

          <CustomInput
            title={t("date")}
            name="date"
            type="date"
            value={deduction.date}
            onChange={(e) => {
              if (validationError === "dateRequired") {
                setValidationError(null);
              }
              setDeduction((prev) => ({
                ...prev,
                date: e.target.value,
              }));
            }}
            flex={true}
            errorText={
              validationError === "dateRequired" ? t("dateRequired") : ""
            }
          />

          <CustomInput
            title={t("deductionDate")}
            name="deductionDate"
            type="date"
            value={deduction?.deductionDate}
            onChange={(e) => {
              if (validationError === "deductionDateRequired") {
                setValidationError(null);
              }
              setDeduction((prev) => ({
                ...prev,
                deductionDate: e.target.value,
              }));
            }}
            flex={true}
            errorText={
              validationError === "deductionDateRequired"
                ? t("deductionDateRequired")
                : ""
            }
          />

          <CustomInputWithUnit
            name="amount"
            title={t("amount")}
            value={selectedDeduction?.price}
            onKeyDown={onlyNums}
            flex={true}
            min={0}
            max={100}
            unit="IQD"
            type="moneyV2"
          />

          <div className="">
            <label>{t("notes")}</label>
            <Textarea
              name="notes"
              value={deduction?.notes}
              onChange={(e) => {
                setDeduction((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }));
              }}
              flex={true}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
